<template>
  <div class="promo-page">
    <h2 class="page-title">當期活動</h2>

    <div v-if="loading" class="hint">載入中…</div>
    <div v-else-if="error" class="hint error">{{ error }}</div>
    <div v-else-if="items.length === 0" class="hint">目前沒有上架中的活動</div>

    <section v-else>
      <article
        v-for="(p, i) in items"
        :key="p.id"
        :id="'promo-' + p.id"
        class="promo-row"
        :class="{
          'with-sep': i > 0,
          'clickable': linkType(p) !== 'none'
        }"
        @click="onCardClick(p)"
      >
        <div class="cover-wrap">
          <img
            :src="p.image_url"
            :alt="p.title"
            class="cover"
            loading="lazy"
            :ref="'cover-' + p.id"
            @load="measureOne(p.id)"
          />
        </div>

        <div class="body">
          <h3 class="title">{{ p.title }}</h3>
          <!-- <p class="time">
            {{ fmt(p.starts_at) }} ～ {{ p.ends_at ? fmt(p.ends_at) : '無期限' }}
          </p> -->

          <!-- 內容包一層，用 max-height + 漸層遮罩 -->
          <div
            class="content-wrap"
            :class="{ clamped: !expanded[p.id] && overflowMap[p.id] }"
            :style="!expanded[p.id] ? { '--clamp-h': (clampHeights[p.id] || defaultClamp) + 'px' } : null"
            :ref="'wrap-' + p.id" 
          >
            <div
              class="content"
              v-html="renderMd(p.content_md)"
              :ref="'content-' + p.id"
            ></div>
          </div>

          <button
            v-if="overflowMap[p.id]"
            type="button"
            class="read-more"
            @click.stop="toggleExpand(p.id)"
          >
            {{ expanded[p.id] ? '收起內容' : '繼續閱讀' }}
          </button>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import { supabase } from '@/lib/supabase'

export default {
  name: 'PromoList',
  data: () => ({
    loading: true,
    error: '',
    items: [],
    // read-more 控制
    expanded: {},        // { [id]: boolean }
    clampHeights: {},    // { [id]: number(px) }
    overflowMap: {},     // { [id]: boolean }
    defaultClamp: 360,   // 找不到圖片高度時的後備值
    rafId: 0,            // ← 原本的 _measureRaf 改名
    scrolledOnce: false // ← 避免重複自動捲動
  }),

  async mounted() {
    try {
      const nowIso = new Date().toISOString()
      const { data, error } = await supabase
        .from('promotions')
        .select('*')
        .in('status', ['scheduled','published'])
        .lte('starts_at', nowIso)
        .or('ends_at.is.null,ends_at.gte.' + nowIso)
        .order('is_featured', { ascending: false })
        .order('starts_at', { ascending: false })

      if (error) throw error
      this.items = data || []
    } catch (e) {
      this.error = e.message || String(e)
    } finally {
      this.loading = false
      this.$nextTick(() => {
        this.measureAll()
        this.scrollToPromo() // ← 載入完成立刻試著捲到指定活動
      })
      window.addEventListener('resize', this.onResize, { passive: true })
    }
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.onResize)
    if (this.rafId) cancelAnimationFrame(this.rafId)
  },

  watch: {
    // 路由 query 變更（例如從公告彈窗點不同張）
    '$route.query.promo'() {
      this.scrolledOnce = false // 允許再次自動捲動
      this.$nextTick(this.scrollToPromo)
    },
    // items 更新（例如重新取得資料）
    items() {
      this.$nextTick(() => {
        this.measureAll()
        this.scrollToPromo()
      })
    }
  },

  methods: {
    fmt(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      const y = d.getFullYear()
      const m = String(d.getMonth()+1).padStart(2,'0')
      const day = String(d.getDate()).padStart(2,'0')
      return `${y}-${m}-${day}`
    },
    renderMd(md) {
      if (!md) return ''
      return md
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>')
    },

    // 取得 v-for 內的 ref，統一還你單一 DOM
    _refOne(key) {
      const r = this.$refs[key]
      return Array.isArray(r) ? r[0] : r
    },

    // ====== 自動捲動到指定活動 ======
    scrollToPromo() {
      if (this.scrolledOnce) return
      const id = this.$route?.query?.promo
      if (!id) return
      // 確認清單中確實有這張卡片
      if (!(this.items || []).some(x => String(x.id) === String(id))) return

      this.$nextTick(() => {
        const el = document.getElementById('promo-' + id)
        if (!el) return
        this.scrolledOnce = true
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        // 高亮 1.2 秒讓使用者有錨點感
        el.classList.add('flash')
        setTimeout(() => el.classList.remove('flash'), 1200)
      })
    },

    linkType(p) {
      if (!p || !p.link_url) return 'none'
      const url = p.link_url.trim()

      if (url.startsWith('http://') || url.startsWith('https://')) {
        return 'external'
      }

      // 其他情況：當作 Event bucket 底下的資料夾
      return 'internal'
    },

    onCardClick(p) {
      const type = this.linkType(p)
      if (type === 'none') return

      if (type === 'external') {
        window.open(p.link_url, '_blank', 'noopener')
        return
      }

      // internal：走你自己的活動詳情頁
      // p.link_url 應該像 "202601invite" 或 "202601invite/"
      const folder = p.link_url.replace(/\/+$/, '') // 去掉結尾斜線
      this.$router.push({
        name: 'PromoDetail',
        params: { folder }
      })
    },

    // ====== 展開/收合 ======
    toggleExpand(id) {
      this.expanded[id] = !this.expanded[id]
      this.$nextTick(() => this.measureOne(id))
    },

    onResize() {
      if (this.rafId) cancelAnimationFrame(this.rafId)
      this.rafId = requestAnimationFrame(this.measureAll)
    },
    measureAll() {
      (this.items || []).forEach(it => this.measureOne(it.id))
    },

    measureOne(id) {
      const coverEl = this._refOne('cover-' + id)
      const wrapEl  = this._refOne('wrap-'  + id)
      if (!wrapEl) return

      const coverH = (coverEl && coverEl.clientHeight) || 0
      const clamp  = coverH > 0 ? Math.min(coverH + 48, 680) : this.defaultClamp
      this.clampHeights[id] = clamp

      const naturalH  = wrapEl.scrollHeight
      const needClamp = naturalH > clamp + 2
      this.overflowMap[id] = needClamp
    },

  }
}
</script>

<style scoped>
.promo-page {
  max-width: 1080px;
  margin: 0 auto;

  /* 主題色系變數 */
  --page-bg: #f3f4f6;   /* 外層淡灰底 */
  --card-bg: #ffffff;   /* 卡片白底 */
  --card-border: #e5e7eb;
}

.page-title {
  margin: 0 0 12px;
  font-weight: 800;
  font-size: 22px;
}
.hint { padding: 12px; color: #555; }
.hint.error { color: #b91c1c; }

/* 整張「卡片」：無陰影、用圓角 + 邊框 + 內距 */
.promo-row {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 20px;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  border-radius: 16px;
  padding: 16px;
  align-items: center;
  /* 與下一張卡片的間距 */
  margin: 16px 0;
}
.promo-row.with-sep { border-top: none; } /* 不再用上分隔線 */

/* 圖片容器不再有陰影，只負責圓角裁切 */
.cover-wrap {
  border-radius: 12px;
  overflow: hidden;
  background: var(--card-bg);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 圖片仍然等比置中 */
.cover {
  width: 100%;
  height: auto;
  max-height: 320px;
  object-fit: contain;
  display: block;
  background: var(--card-bg);
}

.body {
  display: block;
  text-align: left;
}

.title {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
}

.time {
  margin: 0 0 12px;
  font-size: 13px;
  color: #6b7280;
}

/* 內容 + 收合：在卡片內截斷，展開時卡片高度自然延伸 */
.content-wrap {
  position: relative;
  margin-bottom: 12px;
  transition: max-height 220ms ease;
}
.content-wrap.clamped {
  max-height: var(--clamp-h, 360px);
  overflow: hidden;
}
/* 漸層遮罩尾端改用卡片背景，視覺更自然 */
.content-wrap.clamped::after {
  content: "";
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 56px;
  background: linear-gradient(to bottom, rgba(255,255,255,0), var(--card-bg) 70%);
  pointer-events: none;
}

.content {
  font-size: 15px;
  color: #374151;
  line-height: 1.8;
}

/* 繼續閱讀 / 收起 */
.read-more {
  appearance: none;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #111;
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 10px;
  line-height: 1;
  transition: background 0.2s, border-color 0.2s;
}
.read-more:hover { background: #f9fafb; }
.read-more:active { transform: translateY(1px); }

.btn {
  display: inline-block;
  padding: 8px 14px;
  border-radius: 10px;
  background: #111;
  color: #fff;
  text-decoration: none;
  transition: background 0.2s;
}
.btn:hover { background: #222; }
.btn:active { transform: translateY(1px); }

/* 手機版：整張卡片改為直向堆疊 */
@media (max-width: 899px) {
  .promo-row {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    gap: 12px;
    padding: 14px;
    border-radius: 14px;
  }
  .cover { max-height: 240px; }
}

.promo-row.flash {
  box-shadow: 0 0 0 3px rgba(0,113,187,0.2) inset, 0 6px 24px rgba(0,0,0,0.08);
  transition: box-shadow 0.3s ease;
}

.promo-row.clickable {
  cursor: pointer;
}

.promo-row.clickable:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
  transition: all 0.18s ease-out;
}
</style>
